import {InvalidateOptions, RefetchOptions, useQuery, useQueryClient} from '@tanstack/react-query';

import {apiClient} from '../../../../utils/api-client';

function getDocumentRequest(accession: string) {
    return apiClient.get<EditModeDoc>({
        path: `editor-documents-service/api/documents/${accession}/info`
    });
}

export function useDocumentQuery(accession: string | undefined) {
    return useQuery({
        queryKey: ['edit-mode', 'document', accession],
        queryFn: () => {
            if (accession) {
                return getDocumentRequest(accession);
            }
        },
        enabled: Boolean(accession)
    });
}

export function useDocumentQueryCache() {
    const queryClient = useQueryClient();
    const queryKey = ['edit-mode', 'document'];

    function invalidate(accession: string) {
        return queryClient.invalidateQueries({queryKey: [...queryKey, accession]}, {
            refetchType: 'inactive'
        } as InvalidateOptions);
    }

    async function get(accession: string) {
        await invalidate(accession);

        return queryClient.getQueryData<EditModeDoc>([...queryKey, accession]);
    }

    function set(docInfo: EditModeDoc) {
        queryClient.setQueryData<EditModeDoc>([...queryKey, docInfo.accession], docInfo);
    }

    function remove(accession: string) {
        queryClient.removeQueries({queryKey: [...queryKey, accession]});
    }

    function invalidateAll() {
        return queryClient.invalidateQueries(
            {
                queryKey,
                exact: false
            },
            {
                refetchType: 'inactive'
            } as RefetchOptions
        );
    }

    function clearCache() {
        queryClient.removeQueries({queryKey, exact: false});
    }

    return {set, get, remove, invalidate, invalidateAll, clearCache};
}
